// Copyright 2024 praveen.konathala
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     https://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

// Copyright 2024 praveen.konathala

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Popover,
  TextField,
  Autocomplete,
  Checkbox,
  MenuItem,
  FormControlLabel,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { string } from 'prop-types';
import { ClientTypeBrand } from 'src/types/brand';
import { ClientTypeLocation } from 'src/types/location';

interface MultiSelectPopoverProps {
  options: MultiType[];
  label: string;
  buttonLabel: string;
  selectedOptions: MultiType[];
  isMultiSelect?: boolean; // Prop to toggle between multi and single select
  onSelectChange: (selected: MultiType[]) => void;
  isAll?: boolean;
  handleMultiClick?: () => void;
  onApply?: () => void;
}

export type MultiType = ClientTypeBrand | ClientTypeLocation | string;

const MultiSelectPopover: React.FC<MultiSelectPopoverProps> = ({
  options,
  label,
  buttonLabel,
  selectedOptions,
  isMultiSelect = true,
  isAll = true,
  onSelectChange,
  handleMultiClick,
  onApply,
}) => {
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [internalSelected, setInternalSelected] =
    useState<MultiType[]>(selectedOptions);

  useEffect(() => {
    onSelectChange(internalSelected);
  }, [internalSelected]);

  // Open the popover when button is clicked
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    if (handleMultiClick) handleMultiClick();
  };

  // Close the popover
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleApply = () => {
    if (onApply) onApply();
    handleClose();
  };

  // Handle selection changes
  const handleSelectChange = (event: any, value: MultiType[]) => {
    if (isMultiSelect) {
      if (value.includes('All')) {
        if (!internalSelected.includes('All')) {
          setInternalSelected(['All', ...options]);
        } else {
          setInternalSelected(value.filter((item) => item !== 'All'));
        }
      } else {
        if (internalSelected.includes('All') && !value.includes('All')) {
          setInternalSelected([]);
        }
        // If any other option is unchecked while "All" is selected, remove "All"
        else if (
          internalSelected.includes('All') &&
          value.length !== options.length
        ) {
          setInternalSelected(value.filter((item) => item !== 'All'));
        } else {
          setInternalSelected(value);
        }
      }
    } else {
      console.log('value--', value);
      // For single select
      setInternalSelected(value);
      handleClose();
    }
  };
  const isClientTypeLocation = (
    item: MultiType,
  ): item is ClientTypeLocation => {
    return (item as ClientTypeLocation).name !== undefined;
  };

  const isClientTypeBrand = (item: MultiType): item is ClientTypeBrand => {
    return (item as ClientTypeBrand).name !== undefined;
  };

  const isStringType = (item: MultiType): item is string => {
    return (item as string) !== undefined;
  };

  const getlabelFromType = (item: MultiType): string => {
    if (isClientTypeBrand(item)) {
      return (item as ClientTypeBrand).bq_concept_name;
    } else if (isClientTypeLocation(item)) {
      return (item as ClientTypeLocation).bq_account;
    }
    return item;
  };
  const getkeyFromType = (item: MultiType): string => {
    if (isClientTypeBrand(item)) {
      return (item as ClientTypeBrand).bq_concept_name;
    } else if (isClientTypeLocation(item)) {
      return (item as ClientTypeLocation).bq_account;
    }
    if (item === null || item === undefined) {
      return '';
    }
    return item;
  };
  const open = Boolean(anchorEl);

  return (
    <Box>
      <Button
        variant="outlined"
        onClick={handleClick}
        color={theme.palette.primary.contrastText}
        endIcon={<ExpandMoreIcon className={open ? 'rotate-180' : ''} />}
      >
        {buttonLabel}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          <Autocomplete
            key={`lab-${Math.random().toString(36).substr(2, 10)}`}
            multiple={isMultiSelect}
            options={isAll ? ['All', ...options] : [...options]}
            getOptionLabel={(option) => {
              if (!isAll) {
                return option as string;
              }
              if (
                option === null ||
                option === undefined ||
                option[0] == undefined
              ) {
                return 'All';
              }
              return getkeyFromType(option);
            }}
            value={getlabelFromType(internalSelected)}
            onChange={handleSelectChange}
            disableCloseOnSelect={isMultiSelect}
            renderOption={(props, option, { selected }) => (
              <MenuItem
                key={`id-${Math.random().toString(36).substr(2, 9)}`}
                {...props}
              >
                {isMultiSelect && (
                  <FormControlLabel
                    control={
                      <Checkbox checked={selected} style={{ marginRight: 8 }} />
                    }
                    label={getlabelFromType(option)}
                  />
                )}
                {!isMultiSelect && getlabelFromType(option)}
              </MenuItem>
            )}
            renderInput={(params) => <TextField {...params} label={label} />}
          />
        </Box>
        <Button onClick={handleApply}>Apply</Button>
      </Popover>
    </Box>
  );
};

export default MultiSelectPopover;
